import { createAsync, query, revalidate } from "@solidjs/router";
import { createSignal, Show } from "solid-js";

import { getUserFromEvent } from "~/_server/auth.server";
import { ADMIN } from "~/_server/constants";
import { getLastBsdExport, runBsdExport, BSD_EXPORT_KEY } from "~/_server/bsdExport";
import { formattedTimestamp } from "~/lib";

const lastExportQuery = query( async () => {
	"use server";

	return await getLastBsdExport();
}, "getLastBsdExport" );

// The cron route is unauthenticated, so the dashboard goes through this
// admin-checked wrapper rather than fetching the route directly.
const createExport = async () => {
	"use server";

	const user = await getUserFromEvent();

	if ( !user?.groups?.includes( ADMIN ) ) {
		throw new Error( "Unauthorized" );
	}

	return await runBsdExport();
};

const DATE_TIME = { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" };

export const BsdExport = () => {
	const last = createAsync( () => lastExportQuery() );

	const [running, setRunning] = createSignal( false );
	const [message, setMessage] = createSignal<{ text: string; ok: boolean } | null>( null );

	const handleCreate = async () => {
		if ( !confirm( "Build a new BSD register export now?\n\nIf anything has changed since the last export, the new file will be emailed to registerupdates@gov.scot." ) ) return;

		setRunning( true );
		setMessage( null );

		try {
			const r = await createExport();

			setMessage( r.result === "updated"
				? { text: "New export created and sent to the BSD register.", ok: true }
				: { text: "Nothing has changed since the last export, so no file was sent.", ok: true } );

			await revalidate( "getLastBsdExport" );
		} catch ( err ) {
			console.error( "BSD export failed", err );
			setMessage( { text: "The export failed. Check the dev email for details.", ok: false } );
		} finally {
			setRunning( false );
		}
	};

	return (
		<>
			<h2 class="text-xl text-black font-bold mb-4">BSD Register Export</h2>

			<p class="text-sm text-gray-500 mb-1">
				XML register of approved bodies and certifiers for the Scottish Government's Building Standards Division.
			</p>

			<p class="text-sm text-gray-500 mb-4">
				Last export:{" "}
				<Show when={last()?.date} fallback={<span class="italic">none recorded</span>}>
					<span class="font-medium text-gray-700">{formattedTimestamp( last()!.date, DATE_TIME )}</span>
				</Show>
			</p>

			<div class="flex flex-wrap gap-2">
				<a
					href={`/api/download?key=${encodeURIComponent( BSD_EXPORT_KEY )}`}
					class="rias inline-flex items-center bg-sky-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sky-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600"
					classList={{ "pointer-events-none opacity-50": !last()?.date }}
					aria-disabled={!last()?.date}
				>
					Download Latest (XML)
				</a>

				<button
					type="button"
					disabled={running()}
					onClick={handleCreate}
					class="rias inline-flex items-center bg-white px-3 py-2 text-sm font-semibold text-sky-700 ring-1 ring-inset ring-sky-300 shadow-sm hover:bg-sky-50 disabled:opacity-50"
				>
					{running() ? "Building export…" : "Create New Export"}
				</button>
			</div>

			<Show when={message()}>
				{m => <p class="mt-3 text-sm" classList={{ "text-green-700": m().ok, "text-red-600": !m().ok }}>{m().text}</p>}
			</Show>
		</>
	);
};
